import { useSelector } from '@tunasong/redux'
import { vendorsCSP } from '@tunasong/vendor'
import type { FC } from 'react'

function getOrigin(urlString: string): string | null {
  try {
    return new URL(urlString).origin
  } catch {
    return null
  }
}

interface CSPProps {
  domainName?: string
  previewDomainName?: string
  connectSrc?: string[]
  frameSrc?: string[]
  mediaSrc?: string[]
  styleSrc?: string[]
  fontSrc?: string[]
  scriptSrc?: string[]
}

const CSP: FC<CSPProps> = ({
  domainName = 'tunasong.com',
  previewDomainName = 'preview.tunasong.com',
  connectSrc = vendorsCSP.connectSrc,
  mediaSrc = vendorsCSP.mediaSrc,
  frameSrc = vendorsCSP.frameSrc,
  styleSrc = vendorsCSP.styleSrc,
  fontSrc = vendorsCSP.fontSrc,
  scriptSrc = vendorsCSP.scriptSrc,
}) => {
  const csp = {
    'default-src': `'self' blob: *.${domainName} *.${previewDomainName}`,
    'script-src': `'self' 'unsafe-eval' blob: *.${domainName} ${scriptSrc.join(' ')}`,
    'style-src': `'self' 'unsafe-inline' ${styleSrc.join(' ')}`,
    'worker-src': `'self' blob:`,
    'child-src': `'self' blob: ${domainName} *.${domainName} ${previewDomainName} *.${previewDomainName}`,
    'media-src': `'self' blob: data: ${domainName} *.${previewDomainName} ${mediaSrc.join(' ')}`,
    'img-src': `* blob: data: ${domainName} *.${previewDomainName} www.google-analytics.com`,
    'font-src': `'self' data: ${domainName} *.${domainName} *.${previewDomainName} ${fontSrc.join(' ')}`,
    /** ws://127.0.0.1:* and ws://localhost:* are the local kernel host (spec §8.7 item 2) */
    'connect-src': `'self' *.lambda-url.eu-west-1.on.aws blob: s3.eu-west-1.amazonaws.com ${domainName} *.${domainName} *.${previewDomainName} wss://ws.api.${domainName} ws://127.0.0.1:* ws://localhost:* ${connectSrc.join(
      ' '
    )}`,
    'frame-src': `'self' ${domainName}  *.${previewDomainName} ${frameSrc.join(' ')}`,
  }

  const cspContent = Object.entries(csp)
    .map(([key, value]) => `${key} ${value} ;`)
    .join('\n')

  return <meta httpEquiv="Content-Security-Policy" content={cspContent} />
}

/** Render CSP after user config is loaded so external tool origins are included in frame-src.
 * CSP meta tags can only become more restrictive once set, so we must include all
 * origins in the first render.
 *
 * Uses useSelector directly (not useUserConfig) so it can render in main.tsx
 * inside HelmetProvider + Redux Provider, without needing GlobalAppContext. */
export const DynamicCSP: FC = () => {
  const externalTools = useSelector(state => state.config.userConfig.externalTools) ?? []
  const hasLoaded = useSelector(state => state.config.userConfigLoaded)

  const externalOrigins = externalTools.map(t => getOrigin(t.url)).filter(Boolean) as string[]
  const frameSrc = [...vendorsCSP.frameSrc, ...externalOrigins]

  if (!hasLoaded) {
    return null
  }

  return <CSP domainName="tunasong.com" frameSrc={frameSrc} />
}
